import { useMemo } from "react";
import { parseDentes, type ModeloDaFace } from "@bookdental/schemas";
import { Segmentado } from "@/components/face/Segmentado";
import { COR_NEUTRA, condicoesDoPlano, corDoItem } from "./cores-do-plano";
import { FaceDoPlano } from "./face/FaceDoPlano";
import { marcacoesDoPlano } from "./face/marcacoes-do-plano";
import type { ItemDoPlanoTela } from "./ItensDoPlano";
import { OdontogramaDoPlano } from "./OdontogramaDoPlano";
import type { useMontagem } from "./use-montagem";

export type ModoDoDesenho = "DENTES" | "FACE";

/** Colunas do editor: o rosto pede uma coluna da esquerda mais larga que a arcada. */
export function colunasDoEditor(modo: ModoDoDesenho, editavel: boolean) {
  if (modo === "FACE") {
    return editavel
      ? "lg:grid-cols-[340px_minmax(0,1fr)] xl:grid-cols-[340px_minmax(0,1fr)_minmax(0,1.1fr)] 2xl:grid-cols-[420px_minmax(0,1fr)_minmax(0,1.1fr)]"
      : "lg:grid-cols-[340px_minmax(0,1fr)] 2xl:grid-cols-[420px_minmax(0,1fr)]";
  }
  return editavel
    ? "lg:grid-cols-[250px_minmax(0,1fr)] xl:grid-cols-[250px_minmax(0,1fr)_minmax(0,1.1fr)]"
    : "lg:grid-cols-[250px_minmax(0,1fr)]";
}

/**
 * Coluna da esquerda do editor: a arcada (dentes) ou o rosto (modo Face, harmonização).
 */
export function DesenhoDoPlano({
  modo,
  onModo,
  itens,
  destacadoId,
  montagem,
  editavel,
  nascimento,
  modelo,
  onModelo,
  onFixarDestaque,
  onLimparDestaque,
}: {
  modo: ModoDoDesenho;
  onModo: (m: ModoDoDesenho) => void;
  itens: ItemDoPlanoTela[];
  destacadoId: string | null;
  montagem: ReturnType<typeof useMontagem>;
  editavel: boolean;
  nascimento?: Date | string | null;
  modelo: ModeloDaFace;
  onModelo: (m: ModeloDaFace) => void;
  onFixarDestaque: (itemId: string) => void;
  onLimparDestaque: () => void;
}) {
  const condicoes = useMemo(() => condicoesDoPlano(itens, destacadoId), [itens, destacadoId]);
  const outras = useMemo(() => marcacoesDoPlano(itens, destacadoId, montagem.editando?.id ?? null), [itens, destacadoId, montagem.editando]);
  const destacado = itens.find((i) => i.id === destacadoId) ?? null;

  return (
    <div className="space-y-3">
      <Segmentado
        rotulo="Desenho do plano"
        valor={modo}
        onMudar={onModo}
        opcoes={[
          { valor: "DENTES", rotulo: "Dentes" },
          { valor: "FACE", rotulo: "Face" },
        ]}
        className="bg-white dark:bg-card"
      />
      {modo === "FACE" ? (
        <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-border dark:bg-card">
          <FaceDoPlano
            modelo={modelo}
            onModelo={onModelo}
            editavel={editavel}
            outras={outras}
            proprias={montagem.marcacoes}
            onMudar={montagem.setMarcacoes}
            cor={montagem.cor ?? (montagem.editando ? corDoItem(montagem.editando) : COR_NEUTRA)}
            unidade={montagem.unidade}
            chave={montagem.rodada * 1000 + montagem.chave}
            vistaDoDestaque={destacado?.marcacoes[0]?.vista ?? null}
            destacadoNome={destacado?.nome ?? null}
            onClicarItem={onFixarDestaque}
            onLimparDestaque={onLimparDestaque}
          />
        </div>
      ) : (
        <OdontogramaDoPlano
          condicoes={condicoes}
          temItens={itens.length > 0}
          destacado={destacado?.nome ?? null}
          dentesDestacados={parseDentes(destacado?.dentes)}
          nascimento={nascimento}
          onLimparDestaque={onLimparDestaque}
          selecionados={montagem.fdi}
          chave={montagem.chave}
          corDaSelecao={montagem.cor}
          editavel={editavel}
          onSelecionar={(lista) => montagem.selecionar(lista)}
          onArcada={montagem.alternarArcada}
        />
      )}
    </div>
  );
}
